'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import IconFont from '@/components/ui/iconFont';
import { Badge } from '../backpack/components/badge';
import classnames from 'clsx';
import Backpack from '../backpack';
import modalBg from '../images/modalbg.png';
import type { PrimaryTab } from '../backpack/types';
import BonusTab from './bonus-tab';
import { useTranslations } from 'next-intl';
import { useRewardCountManager } from '@/features/reward/store/reward-count';
import { useBagCountManager } from '@/features/reward/store/bag-count';

// ──────────────────────────────────────────────
// 主组件
// ──────────────────────────────────────────────

const ModalName = 'CouponDrawer';

const CouponDrawer = NiceModal.create(() => {
  const t = useTranslations('reward');
  const modal = useModal();
  const [primaryTab, setPrimaryTab] = useState<PrimaryTab>('bonus');

  // Bonus tab 红点：复用入口的奖励计数
  const { count: bonusCount, updateClaimCount } = useRewardCountManager();

  // Coupon tab 红点：背包红点总数（全局共享）
  const { bagCount, updateBagCount } = useBagCountManager();

  const handleClose = () => modal.hide();
  const usedDrawerModal = NiceModal.useModal('user-drawer');
  const blockInteractOutside = useRef(false);

  useEffect(() => {
    if (usedDrawerModal.visible) {
      blockInteractOutside.current = true;
    } else {
      const timer = setTimeout(() => { blockInteractOutside.current = false; }, 300);
      return () => clearTimeout(timer);
    }
  }, [usedDrawerModal.visible]);

  const primaryTabs: { value: PrimaryTab; label: string; count: number; }[] = [
    { value: 'bonus', label: t('bonus'), count: bonusCount },
    { value: 'coupon', label: t('items'), count: bagCount },
  ];

  useEffect(() => {
    updateClaimCount();
    updateBagCount();
  }, []);

  return (
    <Drawer open={modal.visible} onOpenChange={handleClose} direction="bottom">
      <DrawerContent
        className="bg-transparent border-none rounded-t-[16px] text-white h-[88vh] flex flex-col overflow-hidden"
        onInteractOutside={(e) => { if (blockInteractOutside.current) e.preventDefault(); }}
      >

        {/* ── 顶部 Banner ── */}
        <div className="flex relative flex-shrink-0 overflow-hidden">
          <div
            className="absolute right-6 top-1/2 transform -translate-y-1/2 z-10 max-w-48 pb-[15px] leading-6 font-semibold italic text-[19px] text-primary opacity-[0.94]"
            style={{ textShadow: '0px 1px 0px #709FCE, 0px 1px 0px rgba(16,19,27,0.26)' }}
          >
            {t('backpackTitle')}
          </div>
          <Image
            src={modalBg}
            alt="coupon drawer background"
            priority
            className="relative block h-auto z-[-1]"
            sizes="100vw"
          />
          {/* 关闭按钮 */}
          <Button
            type="button"
            variant="default"
            onClick={handleClose}
            className="absolute top-[21%] right-[4%] z-10 h-7 w-7 min-h-0 !rounded-full !bg-transparent !px-0 shadow-none"
          >
            <IconFont name="close" size={13} className='font-medium' color="rgb(169, 181, 203)" />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-3 relative mt-[-10%] bg-surface">
          {/* ── 一级 Tab（Bonus / Coupon，胶囊风格） ── */}
          <div className="flex-shrink-0 mb-[18px] h-[34px] p-[2px] bg-[rgba(0,0,0,0.35)] rounded-[8px] flex items-center text-xs font-medium">
            {primaryTabs.map((tab) => {
              const isActive = primaryTab === tab.value;
              return (
                <Button
                  key={tab.value}
                  size="sm"
                  type="button"
                  variant={isActive ? 'primary' : 'default'}
                  onClick={() => setPrimaryTab(tab.value)}
                  className={classnames(
                    'h-full !font-medium flex-1 gap-1.5 rounded-[8px] !active:scale-100',
                    isActive ? 'shadow-none' : '!bg-transparent shadow-none',
                  )}
                >
                  <div
                    className={classnames('relative',
                      isActive ? 'text-white' : 'text-[#6b7a99]',
                    )}
                    style={
                      isActive
                        ? { textShadow: '-1px -1px #0081c366, 1px -1px #0081c366, -1px 1px #0081c366, 1px 1px #0081c366' }
                        : undefined
                    }
                  >
                    {tab.label}
                    {/* 数量红点 */}
                    <div className='absolute top-[-1px] right-[-16px]'>
                      {
                        tab.count > 0 && <Badge count={tab.count} />
                      }
                    </div>
                  </div>
                </Button>
              );
            })}
          </div>

          {primaryTab === 'bonus' && <BonusTab isVisible={modal.visible} onHide={handleClose} />}
          {primaryTab === 'coupon' && <Backpack onClose={handleClose} />}
        </div>
      </DrawerContent>
    </Drawer>
  );
});

export const showCouponDrawer = (): Promise<undefined> => {
  NiceModal.register(ModalName, CouponDrawer);
  return NiceModal.show(ModalName);
};

export const hideCouponDrawer = () => {
  return NiceModal.hide(ModalName);
};

export default CouponDrawer;
